import { useState } from "react";
import { useTranslation } from "react-i18next";
import { jod } from "@astroplanet/shared";
import { hrApi, useLoan, useLoans } from "../../hooks/hr";
import { useLocale } from "../../hooks/useLocale";
import { formatDateTime, money } from "../../lib/format";
import type { HrLevel, Loan } from "../../lib/typesHr";
import { Empty, SectionHeader } from "../ui/Badges";
import { Modal } from "../ui/Modal";
import { dateOnly, EmployeeSelect, Field, monthFirstIso, ReasonDialog, StatusPill, Table, useAction, useEmpName } from "./shared";

export function LoanRequestDialog({ open, onClose, forOthers = false }: { open: boolean; onClose: () => void; forOthers?: boolean }) {
  const { t } = useTranslation();
  const { act, busy } = useAction();
  const [employeeId, setEmployeeId] = useState("");
  const [kind, setKind] = useState<"advance" | "loan">("advance");
  const [amount, setAmount] = useState("");
  const [count, setCount] = useState("3");
  const [interest, setInterest] = useState("0");
  const [month, setMonth] = useState(monthFirstIso(1));
  const [reason, setReason] = useState("");
  const principal = jod(Number(amount) || 0);
  const total = principal + Math.round((principal * Math.round(Number(interest) * 100)) / 10_000);
  const n = Math.max(1, Math.floor(Number(count) || 1));
  const each = Math.ceil(total / n);
  const valid = principal >= 1000 && n >= 1 && n <= 60 && reason.trim().length >= 3 && month.endsWith("-01") && (!forOthers || !!employeeId);

  async function submit() {
    const r = await act(() => hrApi.post("/loans", { employeeId: forOthers ? employeeId : undefined, kind, principalFils: principal, interestBp: Math.round(Number(interest) * 100), installments: n, firstDeductionMonth: month, reason: reason.trim() }), "hr.loans.requested");
    if (r) onClose();
  }
  return (
    <Modal open={open} title={t("hr.loans.new")} onClose={onClose} footer={<><button type="button" className="btn-ghost" onClick={onClose}>{t("common.cancel")}</button><button type="button" className="btn-primary" disabled={!valid || busy} onClick={() => void submit()}>{t("hr.leave.submit")}</button></>}>
      {forOthers && <Field label={t("hr.employee")}><EmployeeSelect value={employeeId} onChange={setEmployeeId} /></Field>}
      <div className="grid grid-cols-2 gap-3">
        <Field label={t("hr.loans.kind")}><select className="field" value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}><option value="advance">{t("hr.loanKind.advance")}</option><option value="loan">{t("hr.loanKind.loan")}</option></select></Field>
        <Field label={`${t("hr.loans.amount")} (${t("currency")})`}><input className="field num" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="100.000" /></Field>
        <Field label={t("hr.loans.installments")}><input className="field num" inputMode="numeric" value={count} onChange={(e) => setCount(e.target.value)} /></Field>
        <Field label={t("hr.loans.interest")}><input className="field num" inputMode="decimal" value={interest} onChange={(e) => setInterest(e.target.value)} /></Field>
        <Field label={t("hr.loans.firstMonth")}><input type="date" className="field num" value={month} onChange={(e) => setMonth(e.target.value)} /></Field>
      </div>
      {principal > 0 && <p className="rounded-xl bg-purple-50 px-3 py-2 text-xs font-bold text-cosmic-purple">{t("hr.loans.preview", { total: money(total), each: money(each), count: n })}</p>}
      <Field label={t("hr.reason")}><textarea className="field min-h-16" value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
    </Modal>
  );
}

function LoanDetail({ id, level, onClose }: { id: string; level: HrLevel; onClose: () => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const d = useLoan(id);
  const name = useEmpName();
  const { act, busy } = useAction();
  const [repay, setRepay] = useState("");
  const [cancel, setCancel] = useState(false);
  const loan = d.data?.loan;
  return (
    <Modal open size="lg" title={loan ? `${name(loan)} · ${t(`hr.loanKind.${loan.kind}`)}` : t("hr.tabs.loans")} onClose={onClose}>
      {loan && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[[t("hr.loans.principal"), money(loan.principal_fils)], [t("hr.loans.total"), money(loan.total_repayable_fils)], [t("hr.loans.outstanding"), money(loan.outstanding_fils)], [t("hr.loans.installment"), money(loan.installment_fils)]].map(([k, v]) => (
              <div key={String(k)} className="rounded-2xl bg-gray-50 p-3"><div className="text-[10px] font-black uppercase text-gray-500">{k}</div><div className="num font-mono text-lg font-black">{v}</div></div>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2"><StatusPill status={loan.status} /><span className="text-xs text-gray-500">{loan.reason}</span></div>
          <Table head={["#", t("hr.loans.dueMonth"), t("hr.loans.amount"), t("hr.status.label"), ""]}>
            {(d.data?.installments ?? []).map((i) => (
              <tr key={i.id}>
                <td className="num">{i.seq}</td><td className="num">{dateOnly(i.due_month, locale)}</td><td className="num font-bold">{money(i.amount_fils)}</td>
                <td><StatusPill status={i.status} ns="instStatus" />{i.note && <span className="ms-1 text-[10px] text-gray-500">{i.note}</span>}</td>
                <td className="text-end">{level >= 2 && i.status === "scheduled" && <button type="button" className="btn-ghost !px-2 !py-0.5 text-[10px]" disabled={busy} onClick={() => { const to = window.prompt(t("hr.loans.deferPrompt"), monthFirstIso(2)); if (to) void act(() => hrApi.post(`/installments/${i.id}/defer`, { toMonth: to, note: t("hr.loans.deferNote") }), "hr.done"); }}>{t("hr.loans.defer")}</button>}</td>
              </tr>
            ))}
          </Table>
          <h3 className="text-xs font-black uppercase text-gray-500">{t("hr.loans.ledger")}</h3>
          <Table head={[t("hr.date"), t("hr.loans.entry"), t("hr.loans.amount"), t("hr.loans.balance")]}>
            {(d.data?.ledger ?? []).map((l) => <tr key={l.id}><td className="num">{formatDateTime(l.created_at, locale)}</td><td>{t(`hr.loanEntry.${l.entry_type}`, { defaultValue: l.entry_type })}{l.note ? <span className="ms-1 text-[10px] text-gray-500">{l.note}</span> : null}</td><td className={`num font-bold ${l.amount_fils < 0 ? "text-emerald-700" : ""}`}>{money(l.amount_fils)}</td><td className="num">{money(l.balance_after)}</td></tr>)}
          </Table>
          {level >= 2 && loan.status === "approved" && <button type="button" className="btn-primary" disabled={busy} onClick={() => void act(() => hrApi.post(`/loans/${id}/disburse`, { method: "cash" }), "hr.loans.disbursed")}>{t("hr.loans.disburse")}</button>}
          {level >= 2 && loan.status === "active" && (
            <div className="flex items-end gap-2 border-t border-gray-100 pt-3">
              <Field label={`${t("hr.loans.earlyRepay")} (${t("currency")})`}><input className="field num" inputMode="decimal" value={repay} onChange={(e) => setRepay(e.target.value)} /></Field>
              <button type="button" className="btn-success" disabled={busy || !(jod(Number(repay)) > 0)} onClick={() => void act(() => hrApi.post(`/loans/${id}/repay`, { amountFils: jod(Number(repay)) }), "hr.done").then(() => setRepay(""))}>{t("hr.loans.repay")}</button>
            </div>
          )}
          {["requested", "approved"].includes(loan.status) && <button type="button" className="btn-ghost text-red-700" onClick={() => setCancel(true)}>{t("hr.loans.cancel")}</button>}
          <ReasonDialog open={cancel} title={t("hr.loans.cancel")} confirmLabel={t("hr.confirm")} onClose={() => setCancel(false)} onConfirm={() => { setCancel(false); void act(() => hrApi.post(`/loans/${id}/cancel`), "hr.done"); }} />
        </>
      )}
    </Modal>
  );
}

export function LoansTable({ rows, level }: { rows: Loan[]; level: HrLevel }) {
  const { t } = useTranslation();
  const name = useEmpName();
  const [open, setOpen] = useState<string | null>(null);
  return (
    <>
      <Table head={[t("hr.employee"), t("hr.loans.kind"), t("hr.loans.principal"), t("hr.loans.outstanding"), t("hr.loans.installments"), t("hr.status.label")]} empty={rows.length === 0 ? <Empty>{t("hr.loans.none")}</Empty> : undefined}>
        {rows.map((l) => (
          <tr key={l.id} className="cursor-pointer hover:bg-purple-50/40" onClick={() => setOpen(l.id)}>
            <td className="font-black">{name(l)}</td><td>{t(`hr.loanKind.${l.kind}`)}</td><td className="num font-bold">{money(l.principal_fils)}</td><td className="num">{money(l.outstanding_fils)}</td>
            <td className="num">{l.installments_count} × {money(l.installment_fils)}</td>
            <td><StatusPill status={l.status} />{l.status === "requested" && l.waiting_for && <div className="mt-1 text-[10px] font-semibold text-gray-400">{t("hr.waitingFor", { role: t(`hr.role.${l.waiting_for}`, { defaultValue: l.waiting_for }) })}</div>}</td>
          </tr>
        ))}
      </Table>
      {open && <LoanDetail id={open} level={level} onClose={() => setOpen(null)} />}
    </>
  );
}

export function LoansPanel({ level }: { level: HrLevel }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState("");
  const loans = useLoans({ status: status || undefined });
  const [open, setOpen] = useState(false);
  return (
    <section className="card space-y-3 p-5" aria-label={t("hr.tabs.loans")}>
      <SectionHeader icon="💳" title={t("hr.tabs.loans")} subtitle={t("hr.loans.subtitle")}
        right={<div className="flex items-center gap-2">
          <select className="field !w-auto" value={status} onChange={(e) => setStatus(e.target.value)} aria-label={t("hr.status.label")}>
            <option value="">{t("hr.all")}</option>
            {["requested", "approved", "active", "settled", "rejected", "cancelled"].map((s) => <option key={s} value={s}>{t(`hr.status.${s}`)}</option>)}
          </select>
          <button type="button" className="btn-primary" onClick={() => setOpen(true)}>{t("hr.loans.new")}</button>
        </div>} />
      <LoansTable rows={loans.data ?? []} level={level} />
      <LoanRequestDialog open={open} onClose={() => setOpen(false)} forOthers />
    </section>
  );
}
